/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { useFruitSensorData } from "../../../../hooks/useFacilityLocationQueries";
import Plot from "../../../../plotly/Plot";
import { dayjs } from "../../../../utils/date";
import { Loading } from "../../../Helper/Loading";
import useRipeningRoom from "../hooks/useRipeningRoom";
import { AXIS_GROUPS, getAxisGroupKey } from "./axisGroups";
import { ripeningGraphXAxis } from "./ripeningGraphConstants";
import { clampSmaWindow, simpleMovingAverage } from "./smoothing";

// The one visible y-axis per fruit type; all other axes are hidden (values are
// still available via the unified hover). Fruit types not listed show no axis.
const VISIBLE_AXIS_BY_FRUIT_TYPE: Record<string, string> = {
    banana: "color_score",
    avocado: "firmness",
    bio_avocado: "firmness",
    mango: "maturity",
};

// Same as the room sensor graph: timestamps are UTC, shown in the user's local time.
const toLocalTime = (timestamp: string) => dayjs.utc(timestamp).local().format();

const DEFAULT_COLORS = ["#d62728", "#9467bd", "#ff7f0e", "#2ca02c", "#1f77b4", "#17becf", "#8c564b", "#bcbd22"];

export default function FruitSensorDataGraph() {
    const { currentLocation, firstDayInRange, lastDayInRange, fruitSensorConfig, fruit_type } = useRipeningRoom();
    const [isLoading, setIsLoading] = useState(true);

    // Get configured color sensor register keys
    const registerKeys = (fruitSensorConfig || []).map((i) => i.key);
    const configByKey = (fruitSensorConfig || []).reduce((acc: Record<string, any>, item) => {
        acc[item.key] = item;
        return acc;
    }, {});

    // Fetch real RipeTap + register data from backend
    const { data: sensorData, isLoading: isFetching } = useFruitSensorData(
        currentLocation?.location_id,
        firstDayInRange?.format("YYYY-MM-DD"),
        lastDayInRange?.format("YYYY-MM-DD"),
        registerKeys
    );

    useEffect(() => {
        if (!isFetching) {
            setIsLoading(false);
        }
    }, [isFetching]);

    if (isLoading) {
        return <Loading />;
    }

    // Check if we have any data
    const hasRipetapData = sensorData?.ripetap && sensorData.ripetap.length > 0;
    const hasRegisterData = sensorData?.registers && Object.keys(sensorData.registers).length > 0;

    if (!hasRipetapData && !hasRegisterData) {
        return (
            <div className="text-center d-flex align-items-center justify-content-center"
                css={css`min-height: 24rem; background-color: #f8f8f8;`}>
                <p className="text-muted mb-0">
                    No fruit sensor data available<br />
                    Data will appear here when RipeTap or color sensor measurements are taken
                </p>
            </div>
        );
    }

    // Build axis groups in priority order (firmness first, then each register
    // with data). Axis IDs are assigned only after we know which groups are
    // populated, so the primary axis ("y") is always backed by real data —
    // Plotly misbehaves when the primary y-axis has no traces.
    type AxisGroup = {
        key?: string;
        title: string;
        color?: string;
        min?: number;
        max?: number;
        traces: any[]; // traces without `yaxis` assigned yet
    };
    const axisGroups: AxisGroup[] = [];

    // Firmness (RipeTap) group
    if (hasRipetapData) {
        const ripetapData = sensorData.ripetap as any[];

        // Group by measurement
        const measurementGroups: { [key: string]: any[] } = {};
        ripetapData.forEach((tap) => {
            const measurement_id = tap.measurement_id || `tap-${tap.tap_number}`;
            if (!measurementGroups[measurement_id]) {
                measurementGroups[measurement_id] = [];
            }
            measurementGroups[measurement_id].push(tap);
        });

        const firmnessTraces = Object.values(measurementGroups).map((taps, index) => {
            const sortedTaps = (taps as any[]).sort((a, b) => (a.tap_number || 0) - (b.tap_number || 0));
            return {
                type: "scatter",
                mode: "lines+markers",
                x: sortedTaps.map((tap) => toLocalTime(tap.timestamp)),
                y: sortedTaps.map((tap) => tap.firmness),
                name: `Firmness - Measurement ${index + 1}`,
                line: { width: 2, shape: "spline" },
                marker: { size: 4 },
                hovertemplate: "<b>Firmness</b>: %{y:.2f}<br><b>Time</b>: %{x}<extra></extra>",
            };
        });

        if (firmnessTraces.length > 0) {
            axisGroups.push({ key: "firmness", title: "Firmness", traces: firmnessTraces });
        }
    }

    // Register groups (one axis per register key with data)
    if (hasRegisterData && registerKeys.length > 0) {
        const registerData = sensorData.registers as { [key: string]: any[] };
        const sharedGroups: Record<string, AxisGroup> = {};

        registerKeys.forEach((key, keyIndex) => {
            const data = registerData[key] || [];
            if (data.length === 0) {
                return;
            }

            const sortedData = (data as any[]).sort((a, b) => (
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            ));
            const entry = configByKey[key];
            const label = entry?.label || key;
            const override = entry?.color;
            const color = override || DEFAULT_COLORS[keyIndex % DEFAULT_COLORS.length];
            const windowSize = clampSmaWindow(entry?.sma_n);
            const rawValues = sortedData.map((d) => d.value);
            const yValues = windowSize ? simpleMovingAverage(rawValues, windowSize) : rawValues;

            const trace = {
                type: "scatter",
                mode: "lines+markers",
                x: sortedData.map((d) => toLocalTime(d.timestamp)),
                y: yValues as any,
                name: label,
                marker: { color, size: 6 },
                line: { width: 2, color, shape: "spline", smoothing: 1.3 },
                hovertemplate: `<b>${label}</b>: %{y:.2f}<br><b>Time</b>: %{x}<extra></extra>`,
            };

            const groupKey = getAxisGroupKey(key);
            if (groupKey) {
                const existing = sharedGroups[groupKey];
                if (existing) {
                    existing.traces.push(trace);
                    existing.min = existing.min ?? entry?.min;
                    existing.max = existing.max ?? entry?.max;
                    return;
                }
                const groupConfig = AXIS_GROUPS[groupKey];
                sharedGroups[groupKey] = {
                    key: groupKey,
                    title: groupConfig.title,
                    traces: [trace],
                    min: entry?.min ?? groupConfig.range?.[0],
                    max: entry?.max ?? groupConfig.range?.[1],
                };
                axisGroups.push(sharedGroups[groupKey]);
                return;
            }

            axisGroups.push({
                title: label,
                color,
                traces: [trace],
                min: entry?.min,
                max: entry?.max,
            });
        });
    }

    // Now assign axis IDs and build layout entries. Index 0 → primary "y",
    // index 1 → "y2" (right side, overlays "y"), index 2+ → also offset
    // inwards from the right edge.
    const plotData: any[] = [];
    const layout: any = {
        autosize: true,
        showlegend: true,
        dragmode: false,
        hovermode: "x unified",
        plot_bgcolor: "white",
        paper_bgcolor: "white",
        legend: {
            orientation: "h",
            y: 1.08,
            x: 0.5,
            xanchor: "center"
        },
        margin: {
            l: 40,
            r: 40,
            t: 50,
            b: 50,
        },
    };

    const visibleAxisKey = fruit_type ? VISIBLE_AXIS_BY_FRUIT_TYPE[fruit_type] : undefined;

    axisGroups.forEach((group, i) => {
        const yaxisName = i === 0 ? "y" : `y${i + 1}`;
        const layoutKey = i === 0 ? "yaxis" : `yaxis${i + 1}`;

        group.traces.forEach((trace) => {
            plotData.push({ ...trace, yaxis: yaxisName });
        });

        const isVisible = group.key !== undefined && group.key === visibleAxisKey;
        const axisLayout: any = {
            title: group.title,
            side: isVisible ? "left" : "right",
            showgrid: false,
            visible: isVisible,
            zeroline: false,
        };
        // Fixed bounds are configured per register in the device management
        // register configuration screen.
        const hasMin = typeof group.min === "number";
        const hasMax = typeof group.max === "number";
        if (hasMin && hasMax) {
            axisLayout.range = [group.min, group.max];
        } else if (hasMin) {
            axisLayout.minallowed = group.min;
        } else if (hasMax) {
            axisLayout.maxallowed = group.max;
        }
        if (i > 0) {
            axisLayout.overlaying = "y";
        }

        layout[layoutKey] = axisLayout;
    });
    layout.xaxis = ripeningGraphXAxis(plotData);

    return (
        <div>
            <div css={css`min-height: 45rem;`}>
                <Plot
                    data={plotData as any}
                    layout={layout}
                    useResizeHandler={true}
                    style={{ width: "100%", height: "100%", minHeight: "45rem" }}
                    config={{
                        displayModeBar: false,
                        displaylogo: false,
                        responsive: true,
                        scrollZoom: false,
                        doubleClick: false,
                        doubleClickDelay: 0,
                    }}
                />
            </div>
        </div>
    );
}

